import React, { useCallback, useMemo } from 'react'
import { LinkButton } from '@/atoms/Button'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionSM } from '@/atoms/Text'
import { MediaCard } from '@/molecules/MediaCard'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import { useGuildMember } from '@/services/GuildUserService'
import { VideoMetaData } from '@/services/ProjectsService'
import { Media, useFormatDuration } from '@/utils/EpisodeUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface BonusContentTabProps {
  projectSlug: string
  videos: VideoMetaData[]
  movieSpecialMedias: Media[]
  hasWatchableReasons: boolean
}

const MAX_BONUS_CONTENT_DISPLAY = 10

export const BonusContentTab: React.FC<BonusContentTabProps> = ({
  projectSlug,
  videos,
  movieSpecialMedias,
  hasWatchableReasons,
}) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const { guildPermissions } = useGuildMember()
  const userHasEarlyAccess = Boolean(guildPermissions.hasEarlyAccess)
  const formatDuration = useFormatDuration(t)

  const handleClick = useCallback(
    (video: VideoMetaData) => {
      track('Watch Project Bonus Video Clicked', { videoId: video.guid })
    },
    [track],
  )

  const handleViewAllClick = useCallback(() => {
    track('Watch Project Bonus Content View All Clicked', { project: projectSlug })
  }, [track, projectSlug])

  const displayedVideos = useMemo(() => {
    return videos.slice(0, MAX_BONUS_CONTENT_DISPLAY)
  }, [videos])

  const displayedMovieSpecialMedias = useMemo(() => {
    const remainingSlots = MAX_BONUS_CONTENT_DISPLAY - displayedVideos.length
    return remainingSlots > 0 ? movieSpecialMedias.slice(0, remainingSlots) : []
  }, [movieSpecialMedias, displayedVideos.length])

  const totalBonusContent = videos.length + movieSpecialMedias.length
  const hasMoreBonusContent = totalBonusContent > MAX_BONUS_CONTENT_DISPLAY

  return (
    <>
      {displayedVideos.map((video) => {
        const durationLabel =
          typeof video.source.duration === 'number' ? formatDuration(video.source.duration) : undefined
        const href = `/watch/${projectSlug}/videos/${video.page}/${video.guid}`
        let progress = 0

        if (video?.watchPosition?.position && video.source?.duration) {
          progress = (video?.watchPosition?.position / video?.source.duration) * 100
        }
        return (
          <div key={video.guid} className="flex w-full flex-row items-start gap-4">
            <div className="shrink-0 snap-center snap-always">
              <InternalLink href={href} className="relative cursor-pointer" onClick={() => handleClick(video)}>
                <Image
                  alt={`${video.title} Bonus Content Thumbnail`}
                  src={video.posterLandscapeCloudinaryPath || ''}
                  className="h-full max-h-[82px] w-full max-w-[146px] rounded-lg lg:max-h-[177px] lg:max-w-[316px]"
                  width={316}
                  height={177}
                />
                <PlayButtonThumbnailOverlay />
                <ProgressOverlay progress={progress} />
              </InternalLink>
            </div>
            <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:items-start lg:gap-x-0">
              <div className="flex grow-1 flex-col gap-[6px]">
                <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
                  {t('bonus', 'Bonus')}
                </CaptionSM>
                <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
                  {video.subtitle}
                </CaptionSM>
                {durationLabel && (
                  <CaptionSM color="core-gray-500" className="md:photon-paragraph-md">
                    {durationLabel}
                  </CaptionSM>
                )}
              </div>
              <a className="lg:hidden" href={href}>
                <CaretRightIcon size={24} color="white" />
              </a>
            </div>
          </div>
        )
      })}
      {displayedMovieSpecialMedias.map((media, i) => (
        <MediaCard
          className="relative flex min-h-[100px] w-full shrink-0 snap-center snap-always flex-row items-start gap-4"
          thumbnailClassName="h-full w-full min-w-[180px] md:min-w-[256px] lg:min-w-[300px]"
          {...media}
          key={media.guid}
          imgPriority={i <= 4}
          includeHoverScale={false}
          userHasEarlyAccess={userHasEarlyAccess}
          hasWatchableReasons={hasWatchableReasons}
          watchableAvailabilityStatus={media?.watchableAvailabilityStatus ?? null}
          theaterEpisodeCard
        />
      ))}
      {hasMoreBonusContent && (
        <div className="flex justify-start pt-4">
          <LinkButton
            href={`/watch/${projectSlug}/bonus-content`}
            className="mb-16 w-full md:w-fit"
            variant="white"
            outline
            onClick={handleViewAllClick}
          >
            {t('viewAllBonusContent', 'View All Bonus Content')}
          </LinkButton>
        </div>
      )}
    </>
  )
}
